import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import AggregateTable from "./components/AggregateTable"
import FiltersBar from "./components/FiltersBar"
import OverviewStats from "./components/OverviewStats"
import Pagination from "./components/Pagination"
import SessionsPanel from "./components/SessionsPanel"

import {
  useLazyGetAdminSavedToursCompaniesQuery,
  useLazyGetAdminSavedToursOverviewQuery,
} from "../../../app/services/book/admin_saved_tours"

const PER_PAGE = 20
const SEARCH_DEBOUNCE_MS = 350

const App = () => {
  const { t } = useTranslation()

  const [groupBy, setGroupBy] = useState("company")
  const [companyId, setCompanyId] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [q, setQ] = useState("")
  const [debouncedQ, setDebouncedQ] = useState("")
  const [page, setPage] = useState(1)

  const [drilldown, setDrilldown] = useState(null)

  const [fetchCompanies, { data: companiesData }] = useLazyGetAdminSavedToursCompaniesQuery()
  const [fetchOverview, { data, isLoading, isFetching }] = useLazyGetAdminSavedToursOverviewQuery()

  useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [q])

  const queryParams = useMemo(() => {
    const params = { group_by: groupBy, page, per_page: PER_PAGE }
    if (companyId) params.company_id = companyId
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    if (debouncedQ) params.q = debouncedQ
    return params
  }, [groupBy, companyId, dateFrom, dateTo, debouncedQ, page])

  useEffect(() => {
    fetchOverview(queryParams)
  }, [fetchOverview, queryParams])

  useEffect(() => {
    setPage(1)
  }, [groupBy, companyId, dateFrom, dateTo, debouncedQ])

  const rows = data?.rows || []
  const meta = data?.meta
  const overview = data?.overview
  const isInitialLoading = isLoading && rows.length === 0
  const companies = companiesData?.companies || []

  const handleRowClick = (row) => {
    if (groupBy === "company") {
      setDrilldown({ type: "company", id: row.company_id, name: row.company_name })
    } else {
      setDrilldown({ type: "user", id: row.user_id, name: row.user?.name || row.user?.email })
    }
  }

  return (
    <div className="min-h-screen w-full bg-[#F4F6F9] py-6 md:py-10 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 md:mb-6">
          <h1 className="text-lg md:text-xl font-semibold text-[#1F2937]">
            {t("admin_saved_tours.title")}
          </h1>
          <p className="text-sm text-[#9AA5B4] mt-0.5">{t("admin_saved_tours.subtitle")}</p>
        </div>

        <FiltersBar
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          companies={companies}
          companyId={companyId}
          onCompanyChange={setCompanyId}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
          q={q}
          onQChange={setQ}
        />

        <OverviewStats overview={overview} />

        <div className="flex flex-col gap-3">
          {isInitialLoading && (
            <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
              {t("admin_saved_tours.loading")}
            </div>
          )}

          {isFetching && !isInitialLoading && (
            <div className="text-sm text-[#9AA5B4] px-1">{t("admin_saved_tours.updating")}</div>
          )}

          {!isInitialLoading && (
            <AggregateTable groupBy={groupBy} rows={rows} onRowClick={handleRowClick} />
          )}

          {!isInitialLoading && rows.length === 0 && (
            <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
              {t("admin_saved_tours.no_rows")}
            </div>
          )}

          {meta && meta.total_pages > 1 && <Pagination meta={meta} onPageChange={setPage} />}
        </div>
      </div>

      <SessionsPanel
        drilldown={drilldown}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onClose={() => setDrilldown(null)}
      />
    </div>
  )
}

export default App
